import React, { useMemo } from 'react'
import {
  Risk,
  RISK_TREATMENT,
  RISK_MATH,
} from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { InfoTooltip } from 'src/components/Reusable/Tooltip/InfoTooltip'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { Pill } from 'src/components/Reusable/Pill'
import {
  RiskFieldSection,
  RiskResponseContainer,
  TargetLabel,
  FirstLabel,
  RiskPointLabel,
  RisksCaption,
  RiskPillValue,
  RiskPillLabel,
} from '../risks.styles'
import { RisksMathMenu, RisksTreatmentMenu } from '../menus'
import {
  RiskFields,
  RISK_MATH_BG,
  RISK_MATH_LABEL,
  RISK_MATH_LEGEND,
} from '../risks.constants'
import { getRiskMathCalculation } from '../risks.helpers'
import { useActualRiskCalculation } from '../risks.hooks'
import { CalculatedRiskMathChip } from '../risks.components'

export const RisksResponse = ({ risk }: { risk: Risk }): JSX.Element => {
  const {
    targetRiskReductionValue,
    controlEffectiveness,
    actualRiskReductionValue,
    actualRiskReduction,
    actualRiskValue,
  } = useActualRiskCalculation(risk)
  const riskId = risk.getId()
  const impact = risk.getAdverseImpact()
  const likelihood = risk.getLikelihood()
  const targetAdverseImpact = risk.getTargetAdverseImpact()
  const targetLikelihood = risk.getTargetLikelihood()
  const maxResidualRisk = getRiskMathCalculation(impact, likelihood)
  const treatment = risk.getTreatment()
  const showMitigationOptions = useMemo(() => {
    return treatment === RISK_TREATMENT.MITIGATE
  }, [treatment])
  const formatDecimal = (value: number) => {
    return parseFloat(value.toFixed(2))
  }
  const controlEffectivenessPercentage = useMemo(() => {
    return `${formatDecimal(controlEffectiveness * 100)}%`
  }, [controlEffectiveness])

  return (
    <RiskResponseContainer>
      <TargetLabel>Target</TargetLabel>
      <FlexRow justify={FlexAlign.FLEX_START} align={FlexAlign.CENTER} gap={12}>
        <SelectedTreatment riskId={riskId} />
        <TargetLikelihood riskId={riskId} maxResidualRisk={maxResidualRisk} />
        <TargetAdverseImpact
          riskId={riskId}
          maxResidualRisk={maxResidualRisk}
        />
        <TargetResidualRisk
          riskId={riskId}
          targetAdverseImpact={targetAdverseImpact}
          targetLikelihood={targetLikelihood}
        />
        <TargetRiskReduction
          targetRiskReductionValue={targetRiskReductionValue}
        />
      </FlexRow>
      {showMitigationOptions && (
        <>
          <TargetLabel>Current</TargetLabel>
          <FlexRow
            justify={FlexAlign.FLEX_START}
            align={FlexAlign.CENTER}
            gap={12}
          >
            <ControlEffectiveness
              controlEffectivenessPercentage={controlEffectivenessPercentage}
            />
            <CurrentRiskReduction
              actualRiskReductionValue={formatDecimal(actualRiskReductionValue)}
            />
            <CurrentResidualRisk
              actualRiskReduction={actualRiskReduction as RISK_MATH}
              actualRiskValue={formatDecimal(actualRiskValue)}
            />
          </FlexRow>
          <RiskScoreLegend />
          <RisksCaption>
            *The current risk reduction is determined to be{' '}
            {formatDecimal(actualRiskReductionValue)} points, calculated as
            ‘Inherent Risk’ minus current ‘Residual Risk,’ and percentage of
            control effectiveness.{' '}
          </RisksCaption>
        </>
      )}
    </RiskResponseContainer>
  )
}

export const SelectedTreatment = ({
  riskId,
}: {
  riskId: string
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>
        Selected treatment
        <InfoTooltip
          id="selected_treatment"
          body="Show how you plan to deal with this risk."
        />
      </FirstLabel>
      <RisksTreatmentMenu riskId={riskId} />
    </RiskFieldSection>
  )
}

export const TargetLikelihood = ({
  riskId,
  maxResidualRisk,
}: {
  riskId: string
  maxResidualRisk: number
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>Target likelihood</FirstLabel>
      <RisksMathMenu
        riskId={riskId}
        field={RiskFields.targetLikelihood}
        maxValue={maxResidualRisk}
      />
    </RiskFieldSection>
  )
}

export const TargetAdverseImpact = ({
  riskId,
  maxResidualRisk,
}: {
  riskId: string
  maxResidualRisk: number
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>Target impact</FirstLabel>
      <RisksMathMenu
        riskId={riskId}
        field={RiskFields.targetAdverseImpact}
        maxValue={maxResidualRisk}
      />
    </RiskFieldSection>
  )
}

export const TargetResidualRisk = ({
  riskId,
  targetAdverseImpact,
  targetLikelihood,
}: {
  riskId: string
  targetAdverseImpact: number
  targetLikelihood: number
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>
        Target residual risk
        <InfoTooltip
          id="residual_risk"
          body="Overall risk level after response. If you can mitigate, this will generally be lower than the inherent risk."
        />
      </FirstLabel>
      <CalculatedRiskMathChip
        riskId={riskId}
        impact={targetAdverseImpact}
        likelihood={targetLikelihood}
        field={RiskFields.inherentRisk}
        showValue={true}
        $height="40px"
      />
    </RiskFieldSection>
  )
}

export const TargetRiskReduction = ({
  targetRiskReductionValue,
}: {
  targetRiskReductionValue: number
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>Target risk reduction</FirstLabel>
      <Pill
        label={<RiskPillLabel>{targetRiskReductionValue} Points</RiskPillLabel>}
        useBorder={true}
        $width="100%"
        $height="40px"
      />
    </RiskFieldSection>
  )
}

export const ControlEffectiveness = ({
  controlEffectivenessPercentage,
}: {
  controlEffectivenessPercentage: string
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>Control effectiveness</FirstLabel>
      <Pill
        label={<RiskPillLabel>{controlEffectivenessPercentage}</RiskPillLabel>}
        useBorder={true}
        $width="100%"
        $height="40px"
      />
    </RiskFieldSection>
  )
}

export const CurrentRiskReduction = ({
  actualRiskReductionValue,
}: {
  actualRiskReductionValue: number
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>Current risk reduction</FirstLabel>
      <Pill
        label={<RiskPillLabel>{actualRiskReductionValue} Points</RiskPillLabel>}
        useBorder={true}
        $width="100%"
        $height="40px"
      />
    </RiskFieldSection>
  )
}

export const CurrentResidualRisk = ({
  actualRiskReduction,
  actualRiskValue,
}: {
  actualRiskReduction: RISK_MATH
  actualRiskValue: number
}): JSX.Element => {
  return (
    <RiskFieldSection>
      <FirstLabel>Current residual risk</FirstLabel>
      <Pill
        backgroundColor={RISK_MATH_BG[actualRiskReduction]}
        label={
          <FlexRow
            justify={FlexAlign.FLEX_START}
            align={FlexAlign.CENTER}
            gap={8}
          >
            <RiskPillLabel>
              {RISK_MATH_LABEL[actualRiskReduction]}
            </RiskPillLabel>
            <RiskPointLabel>{actualRiskValue} Points</RiskPointLabel>
          </FlexRow>
        }
        $width="100%"
        $height="40px"
      />
    </RiskFieldSection>
  )
}

export const RiskScoreLegend = (): JSX.Element => {
  return (
    <FlexRow
      justify={FlexAlign.FLEX_START}
      align={FlexAlign.CENTER}
      gap={4}
      $mb={8}
      isFullWidth
    >
      {RISK_MATH_LEGEND.map((item) => (
        <Pill
          key={item.label}
          label={
            <RiskPillValue>{`${item.label}: ${item.range[0]} - ${
              item.range[item.range.length - 1] + 1
            }`}</RiskPillValue>
          }
          backgroundColor={item.color}
        />
      ))}
    </FlexRow>
  )
}
